export const HOMEWORK_ENVIRONMENTS = ["Range", "DM", "TDM", "Covax", "Aim Labs", "IRL"];
const ROLE_ORDER = ["Player", "Coach", "Analyst", "Sub", "Manager", "Spectator", "Trialing"];
export function homeworkRoster(roster = []) {
  return roster.map(member => {
    const value = String(member.role || "Player").trim().toLowerCase();
    const role = value === "manger" ? "Manager" : value === "players" ? "Player"
      : ROLE_ORDER.find(name => name.toLowerCase() === value) || "Player";
    return {...member, role};
  }).sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.username.localeCompare(b.username));
}

export function memberIds(team = {}) {
  return [...new Set((team.members || []).map(member => typeof member === "string" ? member : member?.uid).filter(Boolean))];
}

export function homeworkKey(task) { return `${task.teamId}/${task.id}`; }
export function isAssignedTo(task, uid) { return task.scope === "team" || task.assigneeUid === uid; }
export function canComplete(task, uid) { return Boolean(uid && isAssignedTo(task, uid)); }
export function localDay(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function formatDueDate(value) {
  if (!value) return "No due date";
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return `${String(day).padStart(2, "0")}/${date.toLocaleString("en", {month: "short"})}/${year}`;
}
export function homeworkPayload(draft, actor, team, previous = null, now = new Date().toISOString(), revision = `${Date.now()}-${Math.random().toString(36).slice(2)}`) {
  const title = String(draft.title || "").trim();
  const environment = draft.environment || "";
  if (environment && !HOMEWORK_ENVIRONMENTS.includes(environment)) throw new Error("Choose a valid Drills & Exercises environment.");
  const quantity = Number(draft.quantity), unit = String(draft.unit || "").trim();
  if (!title || title.length > 160) throw new Error("Enter a title of up to 160 characters.");
  if (!Number.isFinite(quantity) || quantity <= 0 || quantity > 100000 || !unit || unit.length > 60) throw new Error("Enter a positive quantity and a unit, such as repetitions, minutes or VODs.");
  if (!memberIds(team).includes(actor.uid)) throw new Error("You are no longer a member of this team.");
  if (!["team", "individual"].includes(draft.scope)) throw new Error("Choose Team or Individual Drills & Exercises.");
  if (draft.scope === "individual" && !memberIds(team).includes(draft.assigneeUid)) throw new Error("Select a current member of this team.");
  if (previous && previous.createdByUid !== actor.uid) throw new Error("Only the author can edit this assignment.");
  const dueDate = draft.dueDate || null;
  if (dueDate && (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate) || localDay(new Date(`${dueDate}T12:00:00`)) !== dueDate)) throw new Error("Enter a valid due date.");
  let resourceUrl = String(draft.resourceUrl || "").trim();
  if (resourceUrl && !/^https?:\/\//i.test(resourceUrl)) throw new Error("Resource links must start with https:// or http://.");
  if (resourceUrl) {
    try { resourceUrl = new URL(resourceUrl).href; } catch { throw new Error("Enter a valid resource link."); }
  }
  const description = String(draft.description || "").trim();
  if (description.length > 4000 || resourceUrl.length > 1000) throw new Error("The instructions or resource link are too long.");
  return { title, environment, description, quantity, unit, dueDate, resourceUrl, scope: draft.scope,
    recipientUids:draft.scope==='team'?memberIds(team):[draft.assigneeUid],
    assigneeUid: draft.scope === "individual" ? draft.assigneeUid : null,
    createdByUid: previous?.createdByUid || actor.uid, createdBy: previous?.createdBy || actor.username || "Member",
    createdAt: previous?.createdAt || now, updatedAt: now, revision, signatures: {} };
}

export function visibleHomework(tasks, {uid, search = "", teamId = "", status = "all", due = "all", list = "mine", sort = "manual", order = [], today = localDay()}) {
  const ranks = new Map(order.map((key, index) => [key, index]));
  const result = tasks.filter(task => {
    const signed = Boolean(task.signatures?.[uid]);
    return (list === "sent" ? task.createdByUid === uid : isAssignedTo(task, uid))
      && (!teamId || task.teamId === teamId)
      && (status === "all" || (status === "complete" ? signed : !signed))
      && (due === "all" || (due === "overdue" ? task.dueDate && task.dueDate < today && !signed : due === "today" ? task.dueDate === today : !task.dueDate))
      && `${task.title} ${task.environment || ""} ${task.description} ${task.teamName} ${task.createdBy} ${task.unit}`.toLowerCase().includes(search.trim().toLowerCase());
  });
  return result.sort((a, b) => {
    if (sort === "due") return (a.dueDate || "9999").localeCompare(b.dueDate || "9999") || homeworkKey(a).localeCompare(homeworkKey(b));
    if (sort === "newest") return b.createdAt.localeCompare(a.createdAt) || homeworkKey(a).localeCompare(homeworkKey(b));
    return (ranks.get(homeworkKey(a)) ?? Infinity) - (ranks.get(homeworkKey(b)) ?? Infinity) || a.createdAt.localeCompare(b.createdAt) || homeworkKey(a).localeCompare(homeworkKey(b));
  });
}

export function moveHomework(tasks, order, key, adjacentKey) {
  // Build a complete ordering without dropping hidden or filtered tasks.
  const keys = [...new Set([...order, ...[...tasks].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || homeworkKey(a).localeCompare(homeworkKey(b))).map(homeworkKey)])];
  const a = keys.indexOf(key), b = keys.indexOf(adjacentKey);
  if (a >= 0 && b >= 0) [keys[a], keys[b]] = [keys[b], keys[a]];
  return keys.slice(0, 500);
}
